/**
 * Role predicates — the frontend half of `HexaplaApi.Accounts.Role`.
 *
 * The admin role list used to be written out inline in `router/navGuards.ts`
 * and again in the API's `Plugs.Authenticated`. Two copies of an authorization
 * rule drift. This module is the only place the frontend names a role, and
 * `GET /api/roles` is available if the list ever needs to be fetched rather
 * than mirrored.
 *
 * These are for *presentation* only. Hiding a button is not a security
 * control — every rule here is enforced again server-side by
 * `HexaplaApi.Policy`, which is what actually protects the data.
 */

export const ROLE_SUPER_ADMIN = 'super_admin'
export const ROLE_ADMIN = 'admin'
export const ROLE_SOFTWARE = 'software'
export const ROLE_EDITOR = 'editor'
export const ROLE_CONTRIBUTOR = 'contributor'
export const ROLE_FELLOW = 'fellow'

/** Roles that confer administrative privileges. Mirrors `Role.admin_roles/0`. */
export const ADMIN_ROLES = [ROLE_ADMIN, ROLE_SUPER_ADMIN, ROLE_SOFTWARE]

export const ALL_ROLES = [
  ROLE_SUPER_ADMIN,
  ROLE_ADMIN,
  ROLE_SOFTWARE,
  ROLE_EDITOR,
  ROLE_CONTRIBUTOR,
  ROLE_FELLOW,
]

export const isAdmin = (roles: string[] | undefined | null): boolean =>
  (roles || []).some((r) => ADMIN_ROLES.includes(r))

export const isFellow = (roles: string[] | undefined | null): boolean =>
  (roles || []).includes(ROLE_FELLOW)

/**
 * Whether this contributor can supervise fellows.
 *
 * Not a role — a relationship. Anyone who is not themselves a fellow is
 * eligible; whether they actually supervise anyone depends on data, which is
 * why the store tracks `isSupervisor` separately from this.
 */
export const canSupervise = (roles: string[] | undefined | null): boolean => !isFellow(roles)

/** Human label for a role slug. */
export const roleLabel = (role: string): string =>
  ({
    [ROLE_SUPER_ADMIN]: 'Super Admin',
    [ROLE_ADMIN]: 'Admin',
    [ROLE_SOFTWARE]: 'Software',
    [ROLE_EDITOR]: 'Editor',
    [ROLE_CONTRIBUTOR]: 'Contributor',
    [ROLE_FELLOW]: 'Garret Fellow',
  }[role] || role)

export default { isAdmin, isFellow, canSupervise, roleLabel, ALL_ROLES, ADMIN_ROLES }
